// Module to centralise all communication with AB Tasty tag
import { isStatusRejectedOrForced, isStatusWaiting } from './helpers';
import { targetingToText } from './targetingToText';
import {
  AccountDataTest,
  TagData,
  TagResultTest,
  TestOnPage,
  TestsOnPage,
  TestType,
  Variation,
} from '@src/application/modules/tag/shared/tag/serializedTagTypes';
import { useTagData } from './context/useTagData';
import { tagConditions } from './tagConstants';
import { QATest, QaTestType } from './types';
import { useCallback } from 'react';
import { type TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

export const useTag = () => {
  const { t } = useTranslation();
  const tagData = useTagData();
  return {
    getTests: useCallback(getTests(tagData), [tagData]),
    mappedConditions: useCallback(mappedConditions(tagData, t), [tagData]),
  };
};

const targetPagesOptions = ['selector_scope', 'url_scope', 'code_scope'];
const qaOptions = ['cookie_scope', 'ip_scope'];

const getTestIds = (tagData: TagData) =>
  Object.keys(tagData.results)
    .reduce((filtered: number[], id: string) => {
      if (
        +id &&
        tagData.results[+id].type !== 'mastersegment' &&
        tagData.results[+id].type !== 'multipage' &&
        tagData.results[+id].type !== 'multivariate'
      )
        filtered.push(+id);
      return filtered;
    }, [])
    .reverse();

const getAcceptedTestIds = (testsOnPage: TestsOnPage) =>
  Object.keys(testsOnPage)
    .map((id) => +id)
    .reverse();

type VariationData = {
  name: string;
  id: number;
};

const getExclusionGroupTests = (exclusionGroupId: number, accountDataTests: Record<number, AccountDataTest>) => {
  return Object.entries(accountDataTests).reduce((acc, [id, test]) => {
    if (test.exclusionGroupId === exclusionGroupId)
      return {
        ...acc,
        [id]: test,
      };
    return acc;
  }, {});
};

const getExclusionGroup = (id: number, accountDataTests: Record<number, AccountDataTest>) => {
  const masterTestId = accountDataTests[id].parentID;
  const isSubtest = masterTestId && masterTestId !== 0;

  if (isSubtest) {
    const masterTest = Object.values(accountDataTests).find((accountDataTest) => accountDataTest.id === masterTestId);

    if (!masterTest?.exclusionGroupId) return undefined;

    return {
      id: masterTest?.exclusionGroupId,
      tests: getExclusionGroupTests(masterTest?.exclusionGroupId, accountDataTests),
    };
  }
  if (!accountDataTests[id].exclusionGroupId) return undefined;

  return {
    id: accountDataTests[id].exclusionGroupId,
    tests: getExclusionGroupTests(accountDataTests[id].exclusionGroupId, accountDataTests),
  };
};

const getVariations = (variationsObject: Record<string, Variation> = {}): VariationData[] => {
  const variationIds = Object.keys(variationsObject).map((id) => +id);
  return variationIds.map((id) => ({ name: variationsObject[id].name, id }));
};

const getTargetings = (test: TagResultTest) => {
  const targetingObject = test.targetings;
  const targetingKeys = Object.keys(targetingObject);
  const targetingDetails = {};

  targetingKeys.forEach((key) => {
    switch (key) {
      case 'targetPages':
        if (Object.keys(targetingObject[key]).length > 0) {
          targetingDetails[key] = {
            scopes: targetPagesOptions.map((option) => {
              const placeholderObject = {};
              placeholderObject[option] = targetingObject[key][option]
                ? targetingObject[key][option].conditions.map(({ condition, include, value }) => {
                    return { include, condition: tagConditions[condition], value };
                  })
                : {};
              return placeholderObject;
            }),
          };
        } else targetingDetails[key] = [];
        break;

      case 'segment':
        targetingDetails[key] = targetingObject.segment;
        break;
      case 'qaParameters':
        if (Object.keys(targetingObject[key]).length > 0) {
          targetingDetails[key] = qaOptions.map((option) => {
            const placeholderObject = {};
            placeholderObject[option] = targetingObject[key][option]
              ? targetingObject[key][option].conditions.map(({ include, value, from, range, to }) => {
                  return { include, value, from, range, to };
                })
              : undefined;
            return placeholderObject;
          });
        } else targetingDetails[key] = [];
        break;

      default:
        break;
    }
  });
  return targetingDetails;
};

const getTests =
  (tagData: TagData) =>
  (): {
    acceptedTests: QATest[];
    pendingTests: QATest[];
    rejectedTests: QATest[];
  } => {
    if (!Object.keys(tagData.results).length) return { acceptedTests: [], pendingTests: [], rejectedTests: [] };
    const testIds = getTestIds(tagData);
    const {
      testsOnPage,
      accountData: { tests: accountDataTests },
    } = tagData;
    const acceptedTests = getAcceptedTestIds(tagData.testsOnPage).map((id) => {
      return createQATestInfo(id, accountDataTests, tagData.results[id], testsOnPage[id]);
    });
    const pendingTests = testIds.reduce((filtered: QATest[], id: number) => {
      if (!isStatusWaiting(accountDataTests[id].status)) return filtered;
      return [...filtered, createQATestInfo(id, accountDataTests, tagData.results[id], testsOnPage[id])];
    }, []);

    const rejectedTests = testIds.reduce((filtered: QATest[], id: number) => {
      if (isStatusRejectedOrForced(accountDataTests[id].status))
        return [...filtered, createQATestInfo(id, accountDataTests, tagData.results[id], testsOnPage[id])];
      return filtered;
    }, []);
    return { acceptedTests, pendingTests, rejectedTests };
  };

export function createQATestInfo(
  id: number,
  accountDataTests: Record<number, AccountDataTest>,
  test: TagResultTest,
  testOnPage?: TestOnPage
) {
  const testDatas = accountDataTests[id];
  const { parentID } = testDatas;
  const QAtest = { ...test } as any as QATest;
  const QAModeActive =
    testDatas.scopes !== undefined &&
    ((testDatas.scopes.ipScope && testDatas.scopes.ipScope.length > 0) ||
      (testDatas.scopes.cookieScope !== undefined && testDatas.scopes.cookieScope.length > 0) ||
      testDatas.scopes.qaUrlParameterEnabled);

  const variationsInfo = testOnPage?.testDatas.isAsync
    ? testOnPage.testDatas.asyncVariationInfoById
    : testOnPage?.testDatas.variations;
  if (parentID) {
    const parentTest = accountDataTests[parentID];
    if (parentTest.type === TestType.multipage) QAtest.type = QaTestType.multipage;
    else if (parentTest.type === TestType.mastersegment) QAtest.type = QaTestType.personalization;
    else QAtest.type = QaTestType.multivariate;
    QAtest.parent = {
      id: parentID,
      name: parentTest.name,
      subType: parentTest.sub_type,
    };
  }
  if (testDatas.type === 'ab' && !!testDatas.dynamicTrafficGoalId) QAtest.type = QaTestType.predictive;
  QAtest.id = id;
  QAtest.targetingDetails = getTargetings(test);
  QAtest.variationsData = testOnPage ? getVariations(variationsInfo) : undefined;
  QAtest.exclusionGroup = getExclusionGroup(id, accountDataTests);
  QAtest.QAModeActive = QAModeActive || false;
  QAtest.testDatas = testDatas;

  if (testOnPage) {
    const { variationID = -1, variationName } = testOnPage;
    QAtest.activeVariation = { id: variationID, name: variationID === 0 ? 'Original' : variationName || '' };
  } else {
    QAtest.activeVariation = undefined;
  }

  return QAtest;
}

const triggerCurrentValue = async (tagData: TagData) => {
  return {
    IP: tagData.geoloc?.ip_address?.toString(),
    DEVICE: tagData.parsedUserAgent?.type,
    GEOLOCATION: tagData.geoloc?.country_iso_code,
    BROWSER_LANGUAGE: tagData.browserLanguage,
    SCREEN_SIZE: `${tagData.screenSize}px`,
    BROWSER: tagData.parsedUserAgent?.browser.name,
    LANDING_PAGE: getLandingPage(tagData),
    PREVIOUS_PAGE: getPreviousPageUrl(tagData),
    SOURCE: getReferrer(tagData),
    NUMBER_PAGES_VIEWED: getPageViews(tagData),
    ADBLOCK: usingAdBlock(tagData) ? 'On' : 'Off',
    WEATHER: getWeather(tagData),
  };
};

const mappedConditions = (tagData: TagData, t: TFunction) => (name, conditions) => {
  return conditions.map(
    ({
      value,
      include,
      from,
      to,
      range,
      lang,
      code_country,
      code_city,
      code_least_specific_subdivision,
      code_most_specific_subdivision,
      value_max,
      value_min,
      type_source,
      name: conditionName,
      condition,
      count,
      datalayer_key,
      feedback,
      grade,
      secondary_grade,
      campaign_id,
      variation_id,
      new_visitor,
      segment,
      min_day,
      max_day,
      durationCondition,
      db_name,
      segment_name,
      action,
      category,
      value_custom,
      version,
    }) => {
      return {
        ...targetingToText(
          name,
          include,
          {
            from,
            to,
            range,
            lang,
            value,
            code_country,
            code_city,
            code_least_specific_subdivision,
            code_most_specific_subdivision,
            value_max,
            value_min,
            type_source,
            conditionName,
            condition,
            count,
            datalayer_key,
            feedback,
            grade,
            secondary_grade,
            campaign_id,
            variation_id,
            new_visitor,
            segment,
            min_day,
            max_day,
            durationCondition,
            db_name,
            segment_name,
            action,
            category,
            value_custom,
            version,
          },
          t
        ),
        currentValue:
          name === 'JS_VARIABLE' ? window[conditionName!] || 'undefined' : triggerCurrentValue(tagData)[name],
      };
    }
  );
};

function getSessionCookie(tagData: TagData) {
  const sessionCookie = tagData.ABTastySessionCookie;
  return (
    sessionCookie ||
    (tagData.temporaryCookieValues &&
      tagData.temporaryCookieValues.ABTastySession &&
      tagData.temporaryCookieValues.ABTastySession.value)
  );
}
function getReferrer(tagData: TagData) {
  const sessionCookie = getSessionCookie(tagData);
  return new URLSearchParams(sessionCookie).get('referrer') || 'undefined';
}
function getLandingPage(tagData: TagData) {
  const sessionCookie = getSessionCookie(tagData);
  return new URLSearchParams(sessionCookie).get('lp') || 'undefined';
}
function getPageViews(tagData: TagData) {
  const visitedPages = tagData.ABTastyData?.VisitedPages?.reverse() || [];
  const currentVisit = visitedPages[0]?.visite;
  return visitedPages.reverse().reduce((acc, curr) => (curr.visite === currentVisit ? acc + 1 : acc), 0);
}

function usingAdBlock(tagData: TagData) {
  return tagData.ADBlockEnabled;
}
function getWeather(tagData: TagData) {
  return (
    tagData.geoloc?.weather &&
    `Main: ${tagData.geoloc.weather.main}. Temperature: ${tagData.geoloc.weather.temperature}C`
  );
}
function getPreviousPageUrl(tagData: TagData): string {
  if (document.referrer != null && document.referrer !== '') {
    return document.referrer;
  }
  if (typeof tagData.urlHistory.previous !== 'undefined') {
    return tagData.urlHistory.previous;
  }
  return '';
}
